import { useCallback, useEffect, useRef, useState } from 'react'

import type { OrderTab } from '@/components/pos/pos-types'
import type { CustomerDisplaySyncRequest } from '@/lib/schemas/customer-display-event-schemas'
import {
    DISPLAY_CHANGED_EVENT,
    getDisplayUserId,
} from '@/components/pos/pos-display-picker'
import { useAuth } from '@/hooks/use-auth'
import { posDisplayPublisher } from '@/lib/pos-display-publisher'

/** Only accepted writes are synced; an empty mount is not a clear intent. */
export function usePosDisplaySync(tab: OrderTab) {
    const { isImpersonating } = useAuth()
    const [displayUserId, setDisplayUserId] = useState(getDisplayUserId)
    const [status, setStatus] = useState<
        'idle' | 'pending' | 'synced' | 'error'
    >('idle')
    const [error, setError] = useState<unknown>()
    const generation = useRef(0)
    const previous = useRef<{ json: string; tabID: string }>(undefined)
    const skipClear = useRef(false)
    const latest = useRef<{
        request: CustomerDisplaySyncRequest
        json: string
        generation: number
        checkoutGeneration: number
        started: boolean
    }>(undefined)
    useEffect(() => {
        const changed = () => setDisplayUserId(getDisplayUserId())
        window.addEventListener(DISPLAY_CHANGED_EVENT, changed)
        window.addEventListener('storage', changed)
        return () => {
            window.removeEventListener(DISPLAY_CHANGED_EVENT, changed)
            window.removeEventListener('storage', changed)
        }
    }, [])
    const publish = useCallback(async (replace = false) => {
        const intent = latest.current
        if (!intent) return
        if (
            !replace &&
            intent.checkoutGeneration !==
                posDisplayPublisher.getCheckoutGeneration(
                    intent.request.display_user_id,
                )
        ) {
            latest.current = undefined
            setStatus('idle')
            return
        }
        intent.started = true
        setStatus('pending')
        setError(undefined)
        try {
            const result = await posDisplayPublisher.publish(intent.request, {
                replace,
            })
            if (generation.current !== intent.generation) return
            if (result.status === 'synced') {
                setStatus('synced')
            }
        } catch (failure) {
            if (generation.current !== intent.generation) return
            setError(failure)
            setStatus('error')
        }
    }, [])
    useEffect(() => {
        if (isImpersonating || !displayUserId) {
            generation.current++
            latest.current = undefined
            setStatus('idle')
            return
        }
        const items = tab.items.map((item) => ({
            name: item.product_name,
            qty: item.quantity,
            price: item.unit_price,
            discount: item.discount_percent,
            subtotal:
                item.quantity *
                item.unit_price *
                (1 - item.discount_percent / 100),
        }))
        const request: CustomerDisplaySyncRequest =
            items.length === 0
                ? { display_user_id: displayUserId, type: 'clear' }
                : {
                      display_user_id: displayUserId,
                      type: 'cart',
                      data: {
                          items,
                          total: items.reduce(
                              (sum, item) => sum + item.subtotal,
                              0,
                          ),
                          customer_name: tab.customer?.name ?? '',
                          item_count: items.length,
                      },
                  }
        const json = JSON.stringify(request)
        const initialEmpty = !previous.current && items.length === 0
        const unchanged =
            previous.current?.json === json && previous.current.tabID === tab.id
        if (!unchanged) generation.current++
        previous.current = { json, tabID: tab.id }
        if (initialEmpty || (items.length === 0 && skipClear.current)) {
            skipClear.current = false
            latest.current = undefined
            setStatus('idle')
            return
        }
        skipClear.current = false
        if (unchanged && (!latest.current || latest.current.started)) return
        latest.current = {
            request,
            json,
            generation: generation.current,
            checkoutGeneration:
                unchanged && latest.current
                    ? latest.current.checkoutGeneration
                    : posDisplayPublisher.getCheckoutGeneration(displayUserId),
            started: false,
        }
        const timer = setTimeout(() => {
            void publish()
        }, 300)
        return () => clearTimeout(timer)
    }, [
        tab.id,
        tab.items,
        tab.customer,
        displayUserId,
        isImpersonating,
        publish,
    ])
    const retry = useCallback(() => publish(true), [publish])
    return {
        status,
        error,
        retry,
        skipNextClear: () => {
            skipClear.current = true
        },
        isCheckout: () =>
            Boolean(
                displayUserId && posDisplayPublisher.isCheckout(displayUserId),
            ),
    }
}
